// apps/client/src/ui/fullscreen.tsx (full screen button, 2026-10-18; spec §8.8) — one Full screen button with one platform-aware way
// (boot/install.ts fullscreenWay): where the browser has element full screen (Android, desktop, an iPad) it asks for it inside the tap
// and tries to lock landscape; on an iPhone, whose only full screen is the home-screen app, it opens a short guide (Share, which newer
// iOS can tuck under the ••• menu, then Add to Home Screen, then the new icon). Once full screen or installed there is no button. It sits
// on the rotate card (ui/rotate.tsx), top right on the screens before a town (App) and in the in-town Menu, which can also leave full
// screen. App draws the guide over everything, the rotate card included.
import { useEffect, useSyncExternalStore } from 'react';
import { enterFullscreen, exitFullscreen, fullscreenWay, isFullscreen, type FullscreenWay, type FullscreenWindow } from '../boot/install';
import { useGame } from '../state/store';
import { Expand } from './hud/Icons';
import { useT } from './i18n';
import { Button } from './kit';

const MODES = ['(display-mode: standalone)', '(display-mode: fullscreen)'];
const media = (q: string) => (typeof matchMedia === 'function' ? matchMedia(q) : null);
/** The page as boot/install.ts reads it; a test environment without matchMedia reads as a browser tab. */
function page(): FullscreenWindow | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  return { navigator, document: document as unknown as FullscreenWindow['document'], screen: window.screen as FullscreenWindow['screen'],
    matchMedia: (q) => media(q) ?? { matches: false } };
}
const currentWay = (): FullscreenWay => { const w = page(); return w ? fullscreenWay(w) : 'none'; };
const currentlyFull = (): boolean => { const w = page(); return w ? isFullscreen(w.document) : false; };
function subscribe(fn: () => void): () => void {
  const queries = MODES.flatMap((q) => media(q) ?? []);
  for (const q of queries) q.addEventListener?.('change', fn);
  document.addEventListener('fullscreenchange', fn);
  document.addEventListener('webkitfullscreenchange', fn);
  return () => {
    for (const q of queries) q.removeEventListener?.('change', fn);
    document.removeEventListener('fullscreenchange', fn);
    document.removeEventListener('webkitfullscreenchange', fn);
  };
}
/** The Full screen button's way on this page, kept up to date as it goes full screen or leaves it. */
export function useFullscreenWay(): FullscreenWay {
  return useSyncExternalStore(subscribe, currentWay, () => 'none');
}
/** Whether the page is in element full screen now (the in-town Menu offers Exit full screen then). */
export function useInFullscreen(): boolean {
  return useSyncExternalStore(subscribe, currentlyFull, () => false);
}

/** The Full screen button's tap: the real thing where the browser has it, the guide to the home-screen app on an iPhone. A new way
 *  (boot/install.ts FullscreenWay) is one more case here. */
export function goFullscreen(way: FullscreenWay = currentWay()): void {
  switch (way) {
    case 'api': enterFullscreen(); return;
    case 'homeScreen': useGame.getState().openFullscreenGuide(); return;
    case 'none': return;
    default: { const unknown: never = way; return unknown; }
  }
}
export function leaveFullscreen(): void { exitFullscreen(); }

const CSS = `
.fs-btn { display: inline-flex; align-items: center; justify-content: center; gap: .42em; box-sizing: border-box; min-height: 44px; min-width: 44px;
  font-size: 15px; padding: 9px 16px 10px 13px; white-space: nowrap; }
.fs-btn svg { width: 20px; height: 20px; flex: none; }
/* Top right on the screens before a town (sign-in, the creator, the towns list), clear of the notch side. On a short screen (Safari's
   bars taking room) it is the icon alone, a 44 px round button, so it stays off the panels' top edge; its name stays for screen readers. */
.fs-corner { position: absolute; z-index: 20; top: max(8px, env(safe-area-inset-top)); right: max(12px, env(safe-area-inset-right)); }
@media (max-height: 360px) {
  .fs-corner .fs-btn { width: 44px; padding: 0; border-radius: 50%; }
  .fs-corner .fs-label { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
}
.fs-guide { position: fixed; inset: 0; z-index: 120; display: grid; place-items: center; box-sizing: border-box;
  padding: max(12px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(12px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left)); }
.fs-guide-scrim { position: absolute; inset: 0; background: var(--scrim); animation: fs-fade .25s var(--ease-out); }
.fs-guide-sheet { position: relative; box-sizing: border-box; width: min(760px, 100%); max-height: 100%; overflow: auto; overscroll-behavior: contain;
  background: var(--cream-50); border-radius: var(--r-panel); box-shadow: var(--sh-card); padding: 18px 20px 20px;
  display: grid; grid-template-columns: minmax(0, 1fr) auto; grid-template-areas: 'title done' 'lead lead' 'steps steps'; gap: 8px 16px; align-items: center;
  animation: fs-rise .32s var(--ease-out); }
.fs-guide-sheet h2 { grid-area: title; margin: 0; font-weight: 900; font-size: 24px; line-height: 1.1; letter-spacing: -0.01em; color: var(--wood-800); }
.fs-guide-lead { grid-area: lead; margin: 0; font-weight: 700; font-size: 15px; line-height: 1.4; color: var(--wood-700); text-wrap: pretty; }
.fs-guide-done { grid-area: done; }
.fs-steps { grid-area: steps; list-style: none; margin: 4px 0 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px;
  counter-reset: fs-step; }
.fs-steps li { counter-increment: fs-step; background: var(--cream-100); border-radius: 18px; padding: 10px 12px 12px;
  display: grid; grid-template-columns: 52px minmax(0, 1fr); gap: 10px; align-items: start; }
.fs-steps li > div { display: grid; gap: 3px; }
.fs-steps svg { width: 52px; height: 52px; }
.fs-steps strong { font-weight: 900; font-size: 16px; line-height: 1.2; color: var(--wood-800); text-wrap: balance; }
.fs-steps strong::before { content: counter(fs-step); display: inline-grid; place-items: center; width: 1.3em; height: 1.3em; margin-right: .35em;
  border-radius: 50%; background: var(--leaf-700); color: #fff; font-size: .8em; vertical-align: .08em; }
.fs-steps span { font-weight: 600; font-size: 14px; line-height: 1.35; color: var(--wood-700); text-wrap: pretty; }
/* A phone held upright (the rotate card's Full screen): a sheet up from the foot of the screen, its steps one under another. */
@media (max-aspect-ratio: 1/1) {
  .fs-guide { place-items: end center; padding: max(12px, env(safe-area-inset-top)) 0 0; }
  .fs-guide-sheet { width: min(520px, 100%); border-radius: var(--r-panel) var(--r-panel) 0 0; grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'title' 'lead' 'steps' 'done'; gap: 10px; padding: 22px max(20px, env(safe-area-inset-right)) max(20px, env(safe-area-inset-bottom)) max(20px, env(safe-area-inset-left));
    animation-name: fs-up; }
  .fs-steps { grid-template-columns: minmax(0, 1fr); }
  .fs-steps li { align-items: center; padding: 10px 14px 10px 10px; }
  .fs-steps svg { width: 56px; height: 56px; }
  .fs-guide-done { margin-top: 6px; }
}
@keyframes fs-fade { from { opacity: 0; } }
@keyframes fs-rise { from { opacity: 0; transform: translateY(10px) scale(.98); } }
@keyframes fs-up { from { transform: translateY(40px); opacity: 0; } }
`;
let styled = false;
function injectStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'fullscreen';
  el.textContent = CSS;
  document.head.appendChild(el);
}

/** The in-town Menu's entry: Full screen while the page can still go full screen, Exit full screen once it is (element full screen). */
export function FullscreenMenuEntry() {
  const t = useT();
  const way = useFullscreenWay();
  const full = useInFullscreen();
  if (full) return <Button variant="quiet" onClick={leaveFullscreen}>{t('fullscreen.exit')}</Button>;
  if (way === 'none') return null;
  return <Button variant="quiet" onClick={() => goFullscreen()}>{t('fullscreen.button')}</Button>;
}

/** The compact Full screen button, wherever the page can still go full screen. */
export function FullscreenButton(p: { className?: string }) {
  injectStyle();
  const t = useT();
  const way = useFullscreenWay();
  if (way === 'none') return null;
  return (
    <button type="button" className={`btn btn-quiet fs-btn hit ${p.className ?? ''}`} onClick={() => goFullscreen()}>
      <Expand /><span className="fs-label">{t('fullscreen.button')}</span>
    </button>
  );
}

// The guide's pictures: flat shapes on a 64-unit grid in the world palette (DESIGN.md "Icons"): Safari's toolbar with its Share button
// (and the ••• button it can sit under), the share sheet's Add to Home Screen row, and the new icon among the others.
const pic = { viewBox: '0 0 64 64', 'aria-hidden': true as const, focusable: 'false' as const };
const ink = { fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const SKY_ICON = '#A9DDF3', HILL = '#7CBE6B', WALL = '#FFF4E2';
function ShareStep() {
  return (
    <svg {...pic}>
      <rect x="3" y="19" width="58" height="28" rx="14" fill="#fff" stroke="var(--cream-300)" strokeWidth="2" />
      <path {...ink} d="M13.5 28.5 9 33l4.5 4.5" stroke="var(--wood-400)" strokeWidth="2.6" />
      <circle cx="30" cy="33" r="11" fill="var(--sky-100)" stroke="var(--sun-400)" strokeWidth="3" />
      <path {...ink} d="M26.6 30.6h-.8a1.5 1.5 0 0 0-1.5 1.5v5.4a1.5 1.5 0 0 0 1.5 1.5h8.4a1.5 1.5 0 0 0 1.5-1.5v-5.4a1.5 1.5 0 0 0-1.5-1.5h-.8M30 34.6v-9.2M27.3 28 30 25.3l2.7 2.7"
        stroke="var(--sky-700)" strokeWidth="2.2" />
      <circle cx="46" cy="33" r="1.9" fill="var(--wood-600)" /><circle cx="51" cy="33" r="1.9" fill="var(--wood-600)" /><circle cx="56" cy="33" r="1.9" fill="var(--wood-600)" />
    </svg>
  );
}
function AddStep() {
  return (
    <svg {...pic}>
      <rect x="9" y="5" width="46" height="54" rx="9" fill="#fff" stroke="var(--cream-300)" strokeWidth="2" />
      <rect x="15" y="13" width="26" height="4.5" rx="2.25" fill="var(--cream-200)" />
      <rect x="13" y="25" width="38" height="14" rx="6" fill="var(--sky-100)" stroke="var(--sun-400)" strokeWidth="2.4" />
      <rect x="17" y="28" width="8" height="8" rx="2.2" fill="none" stroke="var(--sky-700)" strokeWidth="1.8" />
      <path {...ink} d="M21 30v4M19 32h4" stroke="var(--sky-700)" strokeWidth="1.8" />
      <rect x="29" y="30" width="18" height="4" rx="2" fill="var(--sky-700)" opacity=".55" />
      <rect x="15" y="46" width="30" height="4.5" rx="2.25" fill="var(--cream-200)" />
    </svg>
  );
}
function OpenStep() {
  return (
    <svg {...pic}>
      <rect x="5" y="5" width="22" height="22" rx="6" fill="var(--cream-200)" />
      <rect x="37" y="5" width="22" height="22" rx="6" fill="var(--cream-200)" />
      <rect x="5" y="37" width="22" height="22" rx="6" fill="var(--cream-200)" />
      <rect x="35" y="35" width="26" height="26" rx="7" fill="none" stroke="var(--sun-400)" strokeWidth="2.6" />
      <clipPath id="fs-app-icon"><rect x="37" y="37" width="22" height="22" rx="5.5" /></clipPath>
      <g clipPath="url(#fs-app-icon)">
        <rect x="37" y="37" width="22" height="22" fill={SKY_ICON} />
        <ellipse cx="48" cy="61" rx="16" ry="8" fill={HILL} />
        <rect x="42.5" y="47" width="11" height="8" rx="1.4" fill={WALL} />
        <path d="M41 48.2 48 42.4l7 5.8z" fill="var(--coral-500)" stroke="var(--coral-500)" strokeWidth="1.6" strokeLinejoin="round" />
        <rect x="46.8" y="50.4" width="2.6" height="4.6" rx=".8" fill="#8A6A4F" />
      </g>
    </svg>
  );
}
const STEPS = [['share', ShareStep], ['add', AddStep], ['open', OpenStep]] as const;

/** The short guide to the home-screen app (an iPhone's only full screen), over everything while open. */
export function FullscreenGuide() {
  injectStyle();
  const t = useT();
  const open = useGame((s) => s.fullscreenGuide);
  const close = useGame((s) => s.closeFullscreenGuide);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);
  if (!open) return null;
  return (
    <div className="fs-guide hit">
      <div className="fs-guide-scrim" onClick={close} />
      <section className="fs-guide-sheet" role="dialog" aria-modal="true" aria-labelledby="fs-guide-h">
        <h2 id="fs-guide-h">{t('fullscreen.guideTitle')}</h2>
        <p className="fs-guide-lead">{t('fullscreen.guideLead')}</p>
        <ol className="fs-steps">
          {STEPS.map(([k, Pic]) => (
            <li key={k}>
              <Pic />
              <div><strong>{t(`fullscreen.${k}Title`)}</strong><span>{t(`fullscreen.${k}Body`)}</span></div>
            </li>
          ))}
        </ol>
        {/* A modal sheet takes the focus to its one action. */}
        <button type="button" className="btn btn-primary fs-guide-done" onClick={close} autoFocus>{t('fullscreen.done')}</button>
      </section>
    </div>
  );
}
